import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Buildings } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CrmControls, priorityRank } from "@/components/CrmControls";

const STATUSES = ["New", "Contacted", "Interested", "Demo Given", "Registered", "Property Submitted", "Approved", "Live", "First Booking", "Active", "Follow-up Later", "Not Interested", "Inactive", "Closed"];
const STYLE = {
  New: "bg-amber-100 text-amber-700", Contacted: "bg-blue-100 text-blue-700", Interested: "bg-indigo-100 text-indigo-700",
  Registered: "bg-teal-100 text-teal-700", "Property Submitted": "bg-cyan-100 text-cyan-700", Approved: "bg-green-100 text-green-700",
  "First Booking": "bg-green-100 text-green-700", Active: "bg-primary/10 text-primary", Inactive: "bg-stone-200 text-stone-600",
};

export default function AdminOwnerLeads() {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => { setLeads(null); api.get(`/admin/owner-leads${filter ? `?status=${encodeURIComponent(filter)}` : ""}`).then((r) => setLeads([...r.data].sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority)))); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (id, patch) => { await api.patch(`/admin/owner-leads/${id}`, patch); toast.success("Updated"); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Owner CRM</h1>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ol-filter" className="w-52 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        {leads === null ? (
          <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <SkeletonRow key={i} />)}</div>
        ) : leads.length === 0 ? (
          <EmptyState icon={Buildings} title="No owner leads yet" message="Owner enquiries from the 'List Your Property' page appear here." />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Type</TableHead>
              <TableHead>Location</TableHead><TableHead>Source</TableHead><TableHead>Priority / Staff / Follow-up</TableHead><TableHead>Notes</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {leads.map((l) => (
                <TableRow key={l.id} data-testid={`owner-lead-${l.id}`}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>{l.phone}</TableCell>
                  <TableCell>{l.property_type || "—"}</TableCell>
                  <TableCell>{l.location || "—"}</TableCell>
                  <TableCell className="text-xs text-stone-500">{l.source || "direct"}</TableCell>
                  <TableCell><CrmControls lead={l} onUpdate={update} testidPrefix="owner-lead" /></TableCell>
                  <TableCell className="max-w-[180px]"><Input defaultValue={l.notes} onBlur={(e) => e.target.value !== l.notes && update(l.id, { notes: e.target.value })} placeholder="Add note…" className="h-8 rounded-lg text-xs" /></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => update(l.id, { status: v })}>
                      <SelectTrigger data-testid={`owner-lead-status-${l.id}`} className={`h-8 w-44 rounded-full border-0 text-xs font-semibold ${STYLE[l.status] || "bg-stone-100"}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </DashboardLayout>
  );
}
